import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { AmplitudeParams } from './amplitude.model';

/**
 * Creates amplitude visualization for sine/cosine functions.
 *
 * Features:
 * - Vertical double-arrow line from midline to peak (top amplitude)
 * - Optional vertical double-arrow line from midline to trough (bottom amplitude)
 * - Horizontal dashed midline at y = d
 * - Horizontal dashed lines at top (y = d + |a|) and bottom (y = d - |a|)
 *
 * Subparts:
 * - top: The top amplitude arrow (default visible)
 * - bottom: The bottom amplitude arrow (default hidden)
 * - midline: Horizontal line at y = d
 * - topLine: Horizontal line at y = d + |a|
 * - bottomLine: Horizontal line at y = d - |a|
 */
export class AmplitudeCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'amplitude';

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as AmplitudeParams | undefined;

    if (!params?.of) {
      throw new Error('Amplitude feature requires params.of to specify the parent function');
    }

    const parentElement = context.elements.get(params.of);
    if (!parentElement) {
      throw new Error(`Parent element ${params.of} not found`);
    }

    if (parentElement.type !== 'sine' && parentElement.type !== 'cosine') {
      throw new Error(`Amplitude feature only supports sine and cosine functions, got ${parentElement.type}`);
    }

    const objects: GeometryElement[] = [];

    // Resolve styles for each subpart
    const topStyle = resolveFeatureStyle('line', this.featureType, context, display, ['top']);
    const bottomStyle = resolveFeatureStyle('line', this.featureType, context, display, ['bottom']);
    const midlineStyle = resolveFeatureStyle('line', this.featureType, context, display, ['midline']);
    const topLineStyle = resolveFeatureStyle('line', this.featureType, context, display, ['topLine']);
    const bottomLineStyle = resolveFeatureStyle('line', this.featureType, context, display, ['bottomLine']);

    const topVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['top']);
    const bottomVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['bottom']);
    const midlineVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['midline']);
    const topLineVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['topLine']);
    const bottomLineVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['bottomLine']);

    // Get the bounding box to determine line extent
    const bbox = board.getBoundingBox();
    const xMin = bbox[0];
    const xMax = bbox[2];

    // Helper to get function params
    const getParams = () => parentElement.getParams?.() ?? {};

    // Get the x position for amplitude arrows (at first maximum)
    const getAmplitudeX = () => {
      const p = getParams();
      const b = p['b'] ?? 1;
      const c = p['c'] ?? 0;
      // For sine: first max at c + π/(2b)
      // For cosine: first max at c (when a > 0) or c + π/b (when a < 0)
      if (parentElement.type === 'sine') {
        return c + Math.PI / (2 * b);
      } else {
        const a = p['a'] ?? 1;
        return a >= 0 ? c : c + Math.PI / b;
      }
    };

    // Midline: horizontal dashed line at y = d
    const midlineResult = elementFactory.line({
      a: [xMin, () => getParams()['d'] ?? 0],
      b: [xMax, () => getParams()['d'] ?? 0],
      name: 'Midline',
      style: {
        ...midlineStyle,
        visible: midlineVisible,
      },
    });
    objects.push(midlineResult.line);
    midlineResult.a.setAttribute({ visible: false });
    midlineResult.b.setAttribute({ visible: false });
    if (midlineResult.lineLabel) {
      objects.push(midlineResult.lineLabel);
    }

    // Top line: horizontal dashed line at y = d + |a|
    const topLineResult = elementFactory.line({
      a: [
        xMin,
        () => {
          const p = getParams();
          return (p['d'] ?? 0) + Math.abs(p['a'] ?? 1);
        },
      ],
      b: [
        xMax,
        () => {
          const p = getParams();
          return (p['d'] ?? 0) + Math.abs(p['a'] ?? 1);
        },
      ],
      name: 'TopLine',
      style: {
        ...topLineStyle,
        visible: topLineVisible,
      },
    });
    objects.push(topLineResult.line);
    topLineResult.a.setAttribute({ visible: false });
    topLineResult.b.setAttribute({ visible: false });
    if (topLineResult.lineLabel) {
      objects.push(topLineResult.lineLabel);
    }

    // Bottom line: horizontal dashed line at y = d - |a|
    const bottomLineResult = elementFactory.line({
      a: [
        xMin,
        () => {
          const p = getParams();
          return (p['d'] ?? 0) - Math.abs(p['a'] ?? 1);
        },
      ],
      b: [
        xMax,
        () => {
          const p = getParams();
          return (p['d'] ?? 0) - Math.abs(p['a'] ?? 1);
        },
      ],
      name: 'BottomLine',
      style: {
        ...bottomLineStyle,
        visible: bottomLineVisible,
      },
    });
    objects.push(bottomLineResult.line);
    bottomLineResult.a.setAttribute({ visible: false });
    bottomLineResult.b.setAttribute({ visible: false });
    if (bottomLineResult.lineLabel) {
      objects.push(bottomLineResult.lineLabel);
    }

    // Top amplitude arrow: vertical double-arrow from midline to top
    const topArrowResult = elementFactory.line({
      a: [getAmplitudeX, () => getParams()['d'] ?? 0],
      b: [
        getAmplitudeX,
        () => {
          const p = getParams();
          return (p['d'] ?? 0) + Math.abs(p['a'] ?? 1);
        },
      ],
      name: 'A',
      style: {
        ...topStyle,
        visible: topVisible,
        arrows: { first: true, last: true },
      },
    });
    objects.push(topArrowResult.line);
    topArrowResult.a.setAttribute({ visible: false });
    topArrowResult.b.setAttribute({ visible: false });
    if (topArrowResult.lineLabel) {
      objects.push(topArrowResult.lineLabel);
    }

    // Bottom amplitude arrow: vertical double-arrow from midline to bottom
    const bottomArrowResult = elementFactory.line({
      a: [getAmplitudeX, () => getParams()['d'] ?? 0],
      b: [
        getAmplitudeX,
        () => {
          const p = getParams();
          return (p['d'] ?? 0) - Math.abs(p['a'] ?? 1);
        },
      ],
      name: 'A',
      style: {
        ...bottomStyle,
        visible: bottomVisible,
        arrows: { first: true, last: true },
      },
    });
    objects.push(bottomArrowResult.line);
    bottomArrowResult.a.setAttribute({ visible: false });
    bottomArrowResult.b.setAttribute({ visible: false });
    if (bottomArrowResult.lineLabel) {
      objects.push(bottomArrowResult.lineLabel);
    }

    return {
      id,
      type: 'amplitude',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
